import { Theme, css } from '@emotion/react';

import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { Radius, Spacing, Typography } from 'chatie-design-system';

import EFinderImage, {
  EFinderImageSizes,
} from '@components/common/EFinderImage';
import SvgIcon from '@components/common/SvgIcon';
import SwipeableListContainer from '@components/common/SwipableListContainer';

import ProfileImg from '@public/icons/profile_32.svg';

import { IProfileByInfoPlacardFormGallery } from '@base/outlookModel';
import { Platform } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { toQueryString } from '@utils/misc';
import { useWebView } from '@utils/webview';

import OutlookButton from './OutlookButton';

const containerCSS = (rows: number) => css`
  .header {
    display: flex;
    justify-content: space-between;
    padding: 10px ${Spacing.medium}px 0;
  }
  .gridContainer {
    display: grid;
    grid-auto-flow: column;
    grid-template-rows: repeat(${rows}, 1fr);
    column-gap: ${Spacing.xsmall}px;
    row-gap: 10px;
    padding: 6px 16px ${Spacing.large}px;
    margin-top: ${Spacing.medium}px;
  }
`;
const cardContainerCSS = (theme: Theme) => css`
  display: flex;
  flex: 1;
  width: 300px;
  padding: ${Spacing.small}px;
  border-radius: ${Radius.small}px;

  box-shadow: 0px 0px 10px 2px rgba(0, 0, 0, 0.05);

  .imageContainer {
    width: 50px;
    height: 50px;
    border-radius: ${Radius.small}px;
    margin-right: ${Spacing.small}px;
    position: relative;
    overflow: hidden;
  }
  .image {
    width: 50px;
    height: 50px;
    background-color: ${theme.var.gray200};
    border-radius: ${Radius.small}px;
    margin-right: ${Spacing.small}px;
  }
  .infoContainer {
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;
    overflow: hidden;
  }
  .name {
    ${Typography.body12};
  }
  .info {
    ${Typography.body11};
    color: ${theme.var.gray500};
    margin-top: ${Spacing.xxsmall}px;
    margin-bottom: ${Spacing.xsmall}px;
  }
  .desc {
    width: 100%;
    ${Typography.h6};
    font-size: 12px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;

interface ProfileByInfoPlacardFormGalleryProps {
  data: IProfileByInfoPlacardFormGallery;
  componentKey: string;
  onClickCard?: (id: number) => void;
  onClickProfile?: (id: number) => void;
}

const ProfileByInfoPlacardFormGallery = ({
  data,
  componentKey,
  onClickCard,
  onClickProfile,
}: ProfileByInfoPlacardFormGalleryProps) => {
  const { t } = useTranslation();
  const { title, row_count, profiles, button } = data;
  const { web } = useWebView();
  const { platform, isCheming } = useGlobalContext();
  const router = useRouter();

  const handleClickOutlookButton = () => {
    if (button.url && button.url !== '') {
      if (platform === Platform.web) {
        router.push(button.url);
      }
      web.openPage(button.url);
    } else {
      const url = `/play/characters/${toQueryString({ componentKey })}`;
      if (platform === Platform.web) {
        router.push(url);
      }
      web.openPage(url);
    }
  };

  return (
    <div css={containerCSS(row_count)}>
      <div className="header">
        <h5>{t(title)}</h5>
        {button && (
          <OutlookButton
            type={button.type as 'arrow'}
            text={button.text}
            onClickButton={handleClickOutlookButton}
          />
        )}
      </div>
      <SwipeableListContainer className="galleryCardListContainer">
        <div className="gridContainer">
          {profiles.map((item, idx) => (
            <div
              css={cardContainerCSS}
              key={`info_list_${idx}`}
              onClick={() =>
                isCheming || item?.payload?.dynamic_object?.type === 'official'
                  ? onClickProfile && onClickProfile(item.id)
                  : onClickCard && onClickCard(item.id)
              }
            >
              <div className="imageContainer">
                {item.image && item.image.key ? (
                  <EFinderImage
                    image={item.image}
                    h={EFinderImageSizes.avatar}
                    alt={'profile_img'}
                  />
                ) : (
                  <div className="image">
                    <SvgIcon icon={ProfileImg} d={50} />
                  </div>
                )}
              </div>
              <div className="infoContainer">
                <h6 className="name">{item.title}</h6>
                <p className="info">{item.information.sub_title}</p>
                <p className="desc">{item.information.description}</p>
              </div>
            </div>
          ))}
        </div>
      </SwipeableListContainer>
    </div>
  );
};

export default ProfileByInfoPlacardFormGallery;
